/**
 * <ProfilePill> — qui est connecté, et qu'on peut en changer (molecule).
 *
 * Une pastille pressable : l'image du profil, une légende (« Espace parent »), le prénom, et le
 * chevron qui annonce la bascule. Elle remplace le titre du header — le nom de l'espace vit
 * désormais dans la pastille, pas dans une barre de titre.
 *
 * Composée d'atomes existants (`<Avatar>` + `<Text>` + `<Icon>`) ; l'ouverture de la feuille de
 * bascule appartient à l'appelant (`onPress`).
 *
 * L'espace élève la reprend telle quelle : sans légende (vide, elle ne prend aucune place), avec
 * `alert` pour le point « quelque chose t'attend dans ton compte », sans `chevron` et avec l'avatar
 * signé (`signed`) : elle ouvre la feuille « moi », dont la tête est le même avatar signé, en grand
 * (Wajih, 16/10). Le chevron annonçait une liste de profils ; l'avatar dit « moi ». Le
 * pictogramme de sa formule se pose au coin bas-droit de l'avatar (`badgeSource`), comme sur
 * l'en-tête d'aujourd'hui (`StudentMainMenuHeader.js:226-232`).
 */
import React from "react"
import { Image, Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Avatar } from "../../atoms/Avatar"
import { Icon } from "../../atoms/icons/Icon"
import { NotificationDot } from "../../atoms/NotificationDot"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"

export type ProfilePillProps = {
    /** Prénom affiché en gras. */
    name: string
    /** Petite légende au-dessus du prénom — défaut « Espace parent ». Vide : pas de ligne. */
    legend?: string
    /** Image empaquetée dans l'app (import PNG). */
    avatarSource?: number
    /** Photo distante, si l'appelant en a une plutôt qu'un asset. */
    avatarUri?: string
    /** Bascule en cours : la pastille ne répond plus, sans changer d'aspect. */
    busy?: boolean
    /** Désactive la bascule (impersonation : la pastille reste décorative). */
    disabled?: boolean
    /** Un point sur l'avatar : quelque chose attend dans le compte. Défaut `false`. */
    alert?: boolean
    /**
     * Une petite image au coin bas-droit de l'avatar — le pictogramme de la formule de l'élève
     * (import PNG). Absente : rien.
     */
    badgeSource?: number
    /** Le chevron qui annonce la bascule. Défaut `true` ; `false` quand la pastille ouvre la feuille « moi ». */
    chevron?: boolean
    /** L'avatar signé (le nœud) : la porte vers « moi », chez l'élève. Défaut `false`. */
    signed?: boolean
    onPress?: () => void
    /** Libellé lu par les lecteurs d'écran — défaut « Changer de profil ». */
    accessibilityLabel?: string
    testID?: string
}

/** Taille de l'avatar dans la pastille — le 34 de la table du livre. */
const AVATAR_SIZE = 34

/** Le pictogramme de la formule : la moitié de l'avatar, qui déborde un peu de son coin. */
const BADGE_SIZE = 16
const BADGE_OVERHANG = 3

export function ProfilePill(props: ProfilePillProps) {
    const {
        name,
        legend = "Espace parent",
        avatarSource,
        avatarUri,
        busy = false,
        disabled = false,
        alert = false,
        badgeSource,
        chevron = true,
        signed = false,
        onPress,
        accessibilityLabel = "Changer de profil",
        testID,
    } = props

    const inert = disabled || busy

    return (
        <Pressable
            testID={testID}
            onPress={onPress}
            disabled={inert}
            accessibilityRole="button"
            accessibilityLabel={accessibilityLabel}
            accessibilityState={{ disabled: inert }}
            style={({ pressed }: { pressed: boolean }) => [
                styles.pill(disabled),
                pressed && !inert ? styles.pressed : undefined,
            ]}
        >
            <Row gap="2" align="center">
                <View>
                    <Avatar source={avatarSource} uri={avatarUri} name={name} size={AVATAR_SIZE} signed={signed} />
                    {badgeSource != null && (
                        <View style={styles.badge} pointerEvents="none">
                            <Image source={badgeSource} style={styles.badgeImage} resizeMode="contain" />
                        </View>
                    )}
                    {alert && (
                        <View style={styles.alert} pointerEvents="none">
                            <NotificationDot />
                        </View>
                    )}
                </View>
                <Stack gap="0">
                    {!!legend && (
                        <Text size="text-xxs" color="text-muted">
                            {legend}
                        </Text>
                    )}
                    {/* Sans prénom (le parent n'a rempli aucune fiche), la légende suffit. */}
                    {!!name && (
                        <Text size="text-sm" weight="bold" color="primary" numberOfLines={1}>
                            {name}
                        </Text>
                    )}
                </Stack>
                {chevron && <Icon name="chevron-down" size={16} color="text-muted" />}
            </Row>
        </Pressable>
    )
}

const styles = StyleSheet.create((theme) => ({
    /**
     * Pleine, sans contour ni relief : la pastille est une chip (livre V13). Son aplat est celui
     * d'une chip enfant NON sélectionnée, `surface-raised`, et pour la même raison : elle est
     * posée sur le fond de page, où la lavande `surface-subtle` ne se détache plus.
     */
    pill: (disabled: boolean) => ({
        alignSelf: "flex-start" as const,
        flexShrink: 1,
        paddingVertical: theme.spacing["1"],
        paddingLeft: theme.spacing["1"],
        paddingRight: theme.spacing["3"],
        borderRadius: theme.radius.full,
        backgroundColor: theme.colors["surface-raised"],
        opacity: disabled ? 0.4 : 1,
    }),
    pressed: {
        transform: [{ scale: 0.98 }],
    },
    /** Dans le coin bas-droit de l'avatar, à cheval sur son bord, comme l'en-tête d'aujourd'hui. */
    badge: {
        position: "absolute",
        right: -BADGE_OVERHANG,
        bottom: -BADGE_OVERHANG,
    },
    badgeImage: {
        width: BADGE_SIZE,
        height: BADGE_SIZE,
    },
    /** Dans le coin haut-droit de l'avatar, à cheval sur son bord. */
    alert: {
        position: "absolute",
        top: 0,
        right: 0,
    },
}))
